<template>
  <div class="app-container">
    <div class="filter-container">
      <el-button class="filter-item" style="margin-left: 10px" type="primary" icon="el-icon-edit" @click="handleCreate">
        新增帳號
      </el-button>
    </div>

    <el-table v-loading="listLoading" :data="list" border fit highlight-current-row style="width: 100%">
      <el-table-column label="姓名" prop="FULL_NAME" align="center" />
      <el-table-column label="帳號" prop="USERNAME" align="center" />
      <el-table-column label="角色" prop="ROLE" align="center">
        <template slot-scope="{ row }">
          <el-tag class="role-tag" :type="row.ROLE | roleTagFilter">
            {{ row.ROLE | roleFilter }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="狀態" prop="IS_ACTIVE" align="center">
        <template slot-scope="{ row }">
          <el-tag :type="row.IS_ACTIVE ? 'success' : 'danger'">
            {{ row.IS_ACTIVE ? '啟用' : '停用' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="建立時間" prop="CREATE_DATE" align="center">
        <template slot-scope="{ row }">
          <span>{{ row.CREATE_DATE | parseTime('{y}-{m}-{d} {h}:{i}') }}</span>
        </template>
      </el-table-column>
      <el-table-column label="操作" align="center" width="230" class-name="small-padding fixed-width">
        <template slot-scope="{ row }">
          <el-button
            :type="row.ROLE === 'owner' ? '' : 'primary'"
            size="mini"
            :disabled="row.ROLE === 'owner'"
            @click="handleUpdate(row)">
            編輯
          </el-button>
          <el-button
            v-if="row.status != 'deleted'"
            size="mini"
            :type="row.ROLE === 'owner' ? '' : 'danger'"
            :disabled="row.ROLE === 'owner'"
            @click="handleDelete(row)">
            刪除
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog :title="textMap[dialogStatus]" :visible.sync="dialogFormVisible">
      <div class="dialog-content">
        <el-form
          ref="dataForm"
          :rules="formRules"
          :model="temp"
          label-position="left"
          label-width="70px"
          class="dialog-form">
          <el-form-item label="姓名" prop="fullName">
            <el-input v-model="temp.fullName" />
          </el-form-item>
          <el-form-item label="帳號" prop="username">
            <el-input v-model="temp.username" :disabled="dialogStatus === 'update'" />
          </el-form-item>
          <el-form-item :label="dialogStatus === 'create' ? '密碼' : '新密碼'" prop="password">
            <el-input
              v-model="temp.password"
              type="password"
              :placeholder="dialogStatus === 'update' ? '留空表示不修改' : ''" />
          </el-form-item>
          <el-form-item label="角色" prop="role">
            <el-select v-model="temp.role" class="filter-item" placeholder="請選擇" style="width: 100%">
              <el-option v-for="item in roleOptions" :key="item.key" :label="item.display_name" :value="item.key" />
            </el-select>
          </el-form-item>
          <el-form-item label="啟用" style="transform: translateX(10px)">
            <el-switch v-model="temp.isActive" />
          </el-form-item>
        </el-form>

        <el-card class="role-permissions-card">
          <div slot="header" class="role-permissions-card__header">
            <i class="el-icon-info" />
            <span class="role-permissions-card__title">角色權限說明</span>
          </div>
          <div class="role-permissions-content">
            <div v-for="role in allRolePermissionsList" :key="role.key" class="role-permission-item">
              <span
                ><b>{{ role.display_name }}</b
                >：{{ role.description }}</span
              >
            </div>
          </div>
        </el-card>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogFormVisible = false"> 取消 </el-button>
        <el-button type="primary" @click="dialogStatus === 'create' ? createData() : updateData()"> 確認 </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { fetchList, createUser, updateUser, deleteUser } from '@/api/userManagement'
import { parseTime } from '@/utils'

const roleOptions = [
  {
    key: 'admin',
    display_name: '管理員',
    description: '具備完整前後台操作權限，可管理所有使用者帳號。'
  },
  {
    key: 'staff',
    display_name: '工作人員',
    description: '具備前後台操作權限，為保護系統安全，不開放帳號管理功能。'
  },
  { key: 'signer', display_name: '簽署者', description: '僅限登入前台，具備文件檢視與簽署權限。' },
  {
    key: 'api_client',
    display_name: '外部系統',
    description: '專供 API 串接使用（如 HIS），禁止登入任何使用者介面。'
  }
]

const roleKeyValue = roleOptions.reduce((acc, cur) => {
  acc[cur.key] = cur.display_name
  return acc
}, {})

export default {
  name: 'UserManagement',
  filters: {
    roleFilter(role) {
      if (role === 'owner') return '擁有者'
      return roleKeyValue[role]
    },
    roleTagFilter(role) {
      const roleMap = {
        owner: 'danger',
        admin: 'success',
        staff: 'info',
        signer: 'warning',
        api_client: 'primary'
      }
      return roleMap[role]
    }
  },
  data() {
    return {
      list: null,
      listLoading: true,
      temp: {
        id: undefined,
        fullName: '',
        username: '',
        password: '',
        role: 'staff',
        isActive: true
      },
      dialogFormVisible: false,
      dialogStatus: '',
      textMap: {
        update: '編輯帳號',
        create: '新增帳號'
      },
      roleOptions
    }
  },
  computed: {
    allRolePermissionsList() {
      return [
        {
          key: 'owner',
          display_name: '擁有者',
          description: '具備全系統最高權限，系統核心帳號，不可修改或刪除。'
        },
        ...this.roleOptions
      ]
    },
    formRules() {
      const passwordRules = []

      if (this.dialogStatus === 'create') {
        passwordRules.push({
          required: true,
          message: '密碼為必填項',
          trigger: 'blur'
        })
      }

      passwordRules.push({
        validator: this.validatePassword,
        trigger: 'blur'
      })

      return {
        fullName: [{ required: true, message: '姓名為必填項', trigger: 'blur' }],
        username: [{ required: true, message: '帳號為必填項', trigger: 'blur' }],
        password: passwordRules,
        role: [{ required: true, message: '角色為必填項', trigger: 'change' }]
      }
    }
  },
  created() {
    this.getList()
  },
  methods: {
    parseTime,
    getList() {
      this.listLoading = true
      fetchList()
        .then((response) => {
          this.list = response.data
          this.listLoading = false
        })
        .catch(() => {
          this.listLoading = false
        })
    },
    resetTemp() {
      this.temp = {
        id: undefined,
        fullName: '',
        username: '',
        password: '',
        role: 'staff',
        isActive: true
      }
    },
    handleCreate() {
      this.resetTemp()
      this.dialogStatus = 'create'
      this.dialogFormVisible = true
      this.$nextTick(() => {
        this.$refs.dataForm.clearValidate()
      })
    },
    createData() {
      this.$refs.dataForm.validate((valid) => {
        if (valid) {
          createUser(this.temp).then(() => {
            this.dialogFormVisible = false
            this.$notify({
              title: '成功',
              message: '新增成功',
              type: 'success',
              duration: 2000
            })
            this.getList()
          })
        }
      })
    },
    handleUpdate(row) {
      this.temp = {
        id: row.ID,
        fullName: row.FULL_NAME,
        username: row.USERNAME,
        role: row.ROLE,
        isActive: !!row.IS_ACTIVE,
        password: '' // Clear password field for updates
      }
      this.dialogStatus = 'update'
      this.dialogFormVisible = true
      this.$nextTick(() => {
        this.$refs.dataForm.clearValidate()
      })
    },
    updateData() {
      this.$refs.dataForm.validate((valid) => {
        if (valid) {
          const tempData = Object.assign({}, this.temp)
          updateUser(tempData.id, tempData).then(() => {
            this.dialogFormVisible = false
            this.$notify({
              title: '成功',
              message: '更新成功',
              type: 'success',
              duration: 2000
            })
            this.getList()
          })
        }
      })
    },
    handleDelete(row) {
      this.$confirm('確定要刪除此帳號嗎?', '警告', {
        confirmButtonText: '確定',
        cancelButtonText: '取消',
        type: 'warning'
      })
        .then(() => {
          deleteUser(row.ID).then(() => {
            this.$notify({
              title: '成功',
              message: '刪除成功',
              type: 'success',
              duration: 2000
            })
            this.getList()
          })
        })
        .catch(() => {})
    },
    validatePassword(rule, value, callback) {
      if (this.dialogStatus === 'create') {
        if (!value) return callback(new Error('密碼為必填項'))
        if (value.length < 6) return callback(new Error('密碼長度不可小於 6 位數'))
      }

      if (this.dialogStatus === 'update' && value) {
        if (value.length < 6) return callback(new Error('密碼長度不可小於 6 位數'))
      }

      callback()
    }
  }
}
</script>

<style lang="scss" scoped>
.role-tag {
  width: 7em;
}

.dialog-content {
  display: flex;
  flex-direction: row;
  gap: 10px;
}

.dialog-form {
  flex: 1;
  padding: 0 3em;
}

.role-permissions-card {
  flex: 1;
  border-radius: 10px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.3s ease;
  margin-right: 2.5em;
}

.role-permissions-card__header {
  display: flex;
  align-items: center;
  gap: 8px;

  .el-icon-info {
    color: #5b9bd5;
    font-size: 18px;
  }
}

.role-permissions-card__title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.role-permissions-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 4px 0;
}

.role-permission-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 14px;
  color: #606266;
  line-height: 1.6;

  .role-permission-icon {
    color: #388e3c;
    font-size: 16px;
    margin-top: 2px;
    flex-shrink: 0;
  }
}
</style>
